import * as React from 'react';
import type { ReactNode } from 'react';
import type { CellEntry } from '@ooh/contracts';
import { resolveDeadlineDisplay } from '../../reservations/lib/formatters';

export interface TtlRemaining {
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Formats a `TtlRemaining` value as `HH:MM:SS`. Single source of truth shared by
 * `InspectPopover` (`inspect-popover-parts.tsx`) and `CellPopover`
 * (`availability-cell-popover.tsx`) — both render the same TTL countdown text.
 */
export function formatTtlRemaining(remaining: TtlRemaining): string {
  return `${String(remaining.hours).padStart(2, '0')}:${String(remaining.minutes).padStart(2, '0')}:${String(remaining.seconds).padStart(2, '0')}`;
}

/** One calendar day in milliseconds — the availability-calendar capability's
 * countdown/date boundary: a deadline less than this far away renders as a
 * live countdown, a day or more away renders as a plain date instead (an
 * unbounded "312:45:02" HH:MM:SS readout is not readable). */
export const TTL_COUNTDOWN_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Whether `expiresAt` is close enough to warrant a live countdown: strictly
 * in the future AND less than one day away. False for an already-past
 * instant (that state renders the expired-and-awaiting-release treatment
 * instead — see `inspect-popover-parts.tsx`) and false for a distant
 * deadline (renders as a date instead, per the availability-calendar
 * capability's presentation rule).
 */
export function isWithinCountdownWindow(expiresAt: Date, now: Date = new Date()): boolean {
  const diffMs = expiresAt.getTime() - now.getTime();
  return diffMs > 0 && diffMs < TTL_COUNTDOWN_WINDOW_MS;
}

// ─── Deadline field derivation (availability-calendar / reservation-engine) ─

export interface DeadlineFieldLabels {
  /** "Brak terminu" — same marker the reservation lists use for a SOFT/HARD row with no deadline set. */
  noDeadlineSet: string;
  /** "Wygasła — oczekuje na zwolnienie" — same marker the reservation lists use for an elapsed deadline. */
  expiredMarker: string;
}

/**
 * Derives the popover's deadline field content, per the availability-calendar
 * capability's "The expiry deadline reaches the operator through the cell
 * popover" requirement and the reservation-engine capability's "Availability
 * cell popover countdown" requirement:
 * - Entry whose status cannot expire (anything but SOFT/HARD reservation,
 *   e.g. SOLD/OPTION) → `undefined` (no field at all, no marker)
 * - No `ttlExpiresAt` → the same "no deadline set" marker the lists use
 * - Already past → the elapsed date + the same expired-and-awaiting-release
 *   marker the lists use
 * - Less than a day away → the live HH:MM:SS countdown (unchanged behavior)
 * - A day or more away → a plain date, never an unbounded HH:MM:SS readout
 *
 * Uses `React.createElement` (not JSX) so this stays a plain `.ts` module.
 */
export function resolveDeadlineFieldNode(
  status: CellEntry['status'],
  ttlExpiresAt: string | null | undefined,
  ttlRemaining: TtlRemaining | null,
  labels: DeadlineFieldLabels,
): ReactNode | undefined {
  if (status !== 'SOFT_RESERVATION' && status !== 'HARD_RESERVATION') return undefined;

  const normalizedTtl = ttlExpiresAt ?? null;
  const display = resolveDeadlineDisplay(status, normalizedTtl);

  if (display.kind === 'none') return labels.noDeadlineSet;

  if (display.kind === 'expired') {
    return React.createElement(
      React.Fragment,
      null,
      display.formattedDate,
      React.createElement(
        'span',
        { className: 'ml-1 font-medium text-destructive' },
        labels.expiredMarker,
      ),
    );
  }

  // 'scheduled' (normalizedTtl is non-null here — 'none' already returned
  // above) — live countdown inside the final day, a plain date otherwise.
  if (ttlRemaining && normalizedTtl && isWithinCountdownWindow(new Date(normalizedTtl))) {
    return formatTtlRemaining(ttlRemaining);
  }
  return display.formattedDate;
}

export function computeTtlRemaining(expiresAt: Date, now: Date = new Date()): TtlRemaining | null {
  const diffMs = expiresAt.getTime() - now.getTime();
  if (diffMs <= 0) return null;
  const totalSeconds = Math.floor(diffMs / 1_000);
  return {
    hours: Math.floor(totalSeconds / 3_600),
    minutes: Math.floor((totalSeconds % 3_600) / 60),
    seconds: totalSeconds % 60,
  };
}

export function useTtlCountdown(
  ttlExpiresAt: Date | null | undefined,
  onExpired?: () => void,
): TtlRemaining | null {
  const [remaining, setRemaining] = React.useState<TtlRemaining | null>(() =>
    ttlExpiresAt ? computeTtlRemaining(ttlExpiresAt) : null,
  );
  const onExpiredRef = React.useRef(onExpired);
  onExpiredRef.current = onExpired;
  const firedRef = React.useRef(false);

  React.useEffect(() => {
    firedRef.current = false;
    if (!ttlExpiresAt) {
      setRemaining(null);
      return;
    }
    const tick = () => {
      const next = computeTtlRemaining(ttlExpiresAt);
      setRemaining(next);
      if (next === null && !firedRef.current) {
        firedRef.current = true;
        onExpiredRef.current?.();
      }
    };
    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
  }, [ttlExpiresAt]);

  return remaining;
}
